import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// import AboutImg from "../assets/Img/About.avif.avif";
import AboutImg from "../../assets/Img/About.avif.avif";
import CEO from '../../assets/CEO1.jpeg';
import CTO from '../../assets/CTO1.jpeg';

const Counter = ({ target, isPercent }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseInt(target);
    if (start === end) return;

    let duration = 10000; // total duration
    let incrementTime = 100; // interval speed
    let step = Math.ceil(end / (duration / incrementTime));

    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        start = end;
        clearInterval(timer);
      }
      setCount(start);
    }, incrementTime);

    return () => clearInterval(timer);
  }, [target]);

  return (
    <p className="text-5xl font-bold mb-2">
      {count}
      {isPercent ? "%" : "+"}
    </p>
  );
};

const LeadershipCard = ({ leader, index }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const maxLength = 220; // Characters to show before truncating

  const toggleShowMore = () => setShowMore(!showMore);

  const shouldTruncate = leader.bio.length > maxLength;
  const displayedBio =
    showMore || !shouldTruncate
      ? leader.bio
      : `${leader.bio.slice(0, maxLength)}...`;

  return (
    <motion.div
      className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-100 transition-all duration-300 hover:shadow-lg flex flex-col"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      whileHover={{ y: -5 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Image Section */}
      <div className="relative overflow-hidden">
        <img
          src={leader.image}
          alt={leader.name}
          width="400"
          height="320"
          className="w-full h-80  object-cover  transition-transform duration-500"
          style={{ transform: isHovered ? 'scale(1.05)' : 'scale(1)' }}
        />
      </div>

      {/* Content Section */}
      <div className="p-6 transition-all duration-300">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-xl font-semibold text-gray-900">{leader.name}</h4>
            <p className="text-blue-600 text-xs font-medium">{leader.position}</p>
          </div>

          <div className="flex space-x-2">
            {leader.socialLinks?.linkedin && (
              <a
                href={leader.socialLinks.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-500 hover:text-blue-700 transition-colors"
                aria-label="LinkedIn"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            )}
          </div>
        </div>

        {/* Bio Text */}
        <div className=" border-t border-gray-100 transition-all duration-300">
          <p className="text-sm font-sans text-gray-600 leading-relaxed">
            {displayedBio}
          </p>

          {shouldTruncate && (
            <button
              onClick={toggleShowMore}
              className="mt-2 text-blue-600 text-sm font-medium hover:underline focus:outline-none"
            >
              {showMore ? 'Show less' : 'Show more'}
            </button>
          )}
        </div>
      </div>
    </motion.div>
  );
};

const AboutUs = () => {
  // State for collapsible Leadership section
  const [isLeadershipOpen, setIsLeadershipOpen] = useState(false);
  const [activeLeader, setActiveLeader] = useState(null);
  const timeoutRef = useRef(null); // Ref to store timeout ID

  // Leadership data
  const leadershipData = [
    {
      name: "Arindam Das",
      position: "Co-Founder & CEO",
      bio: "With nearly two decades of experience spanning Management Consulting, IT, Fintech, Retail, Manufacturing, and BFSI, Arindam has consistently driven transformation and innovation. Challenging the status quo, he founded VORSTEL with a clear mission — to reinvent the digital engineering landscape with unwavering focus on clients’ strategic goals. Guided by his belief that true success is built on the synergy of people, process, and product, Arindam champions teamwork, integrity, and purposeful innovation at every step.",
      image: CEO,
      socialLinks: {
        linkedin: "https://www.linkedin.com/in/arindamdas01/",
        // twitter: "#"
      }
    },
    {
      name: "Vishesh Malik",
      position: "Co-Founder & CTO",
      bio: "Vishesh has been passionate about delivering complex enterprise solutions across diverse industries and geographies. His extensive expertise in SAP, underpinned by strong problem-solving capabilities and business process re-engineering, led to the idea of co-founding VORSTEL. Vishesh blends technical mastery with a passion for innovation, leading teams to design and implement scalable future-ready digital solutions that drive meaningful impact for clients.",
      // image: "https://media.licdn.com/dms/image/v2/C4E03AQEdPufG6uqJ9g/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1596976138916?e=1758758400&v=beta&t=o2AVgWzkRx0P0lThDyTLxDfRflBkTFqZOsWUwF7zl88",
      image:CTO,
      socialLinks: {
        linkedin: "https://www.linkedin.com/in/vishesh-malik/",
        // twitter: "#"
      }
    },
  ];

  // Effect to handle auto-closing the leadership section
  useEffect(() => {
    // Clear any existing timeout when the section closes or component unmounts
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Effect to set timeout when leadership section opens
  useEffect(() => {
    if (isLeadershipOpen) {
      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // Set new timeout to close after 20 seconds
      timeoutRef.current = setTimeout(() => {
        setIsLeadershipOpen(false);
      }, 500000);
    } else {
      // Clear timeout when section is closed manually
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  }, [isLeadershipOpen]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        when: "beforeChildren"
      }
    }
  };

  const leadershipVariants = {
    hidden: {
      height: 0,
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    visible: {
      height: 'auto',
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        staggerChildren: 0.1
      }
    }
  };

  return (
    <section className="py-16 bg-sky-100/30">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {/* Header without animation */}
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-800 mb-2">
            Who <span className="text-blue-600">We Are</span>
          </h2>
          <div className="w-20 h-1 bg-blue-500 mx-auto mb-4"></div>
          <p className="text-md text-gray-600 max-w-3xl mx-auto">
            We're a passionate team dedicated to creating innovative solutions that make a difference in people's lives.
          </p>
        </div>

        {/* Mission with Image */}
        <div className="flex flex-col md:flex-row gap-12 mb-16">
          <div className="md:w-1/2">
            <div className="bg-white p-8 rounded-xl shadow-sm">
              <h3 className="text-2xl font-bold text-gray-900 mb-6 relative pb-3">
                Our Vision
                <span className="absolute bottom-0 left-0 w-12 h-1 bg-blue-500 rounded-full"></span>
              </h3>
              <p className="text-gray-700 text-lg leading-8 mb-4">
                Vorstel Technologies offers an Open Eco-System for organizations to imagine beyond technological advancement in their respective industries leveraging next generation applications & technologies. Our team brings conviction, experience and agility to solve complex and business critical challenges that yields measurable business value for clients.
              </p>
              <p className="text-gray-700 text-lg leading-8">
                We are a grid to supply deep expertise, solutions and resources in AI leveraged Innovations, SAAS consulting & implementations (SAP, Microsoft, Salesforce), custom software development, enterprise architecture, DevOps, security, cloud & infra as a service. We pride to take over assignments at any stage in a client's digital transformation journey in the fastest time and deliver the expected outcome.
              </p>
            </div>
          </div>

          <div className="md:w-2/5">
            <div className="h-full rounded-xl overflow-hidden shadow-sm border border-gray-100">
              <img
                src={AboutImg}
                alt="Software development team"
                width="600"
                height="500"
                loading="lazy"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        {/* Leadership Section */}
        <div className="mb-10 ">
          <motion.div
            className="flex items-center justify-between cursor-pointer p-6 bg-gray-300 rounded-xl shadow-sm hover:bg-gray-50 transition-colors"
            onClick={() => setIsLeadershipOpen(!isLeadershipOpen)}
            whileHover={{ x: 5 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <h3 className="text-2xl font-bold text-gray-900">
              Leadership
            </h3>
            <motion.div
              animate={{ rotate: isLeadershipOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            >
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </motion.div>
          </motion.div>

          <AnimatePresence>
            {isLeadershipOpen && (
              <motion.div
                variants={leadershipVariants}
                initial="hidden"
                animate="visible"
                exit="hidden"
                className="mt-4 bg-white p-8 rounded-xl overflow-hidden"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-center max-w-3xl mx-auto">
                  {leadershipData.map((leader, index) => (
                    <LeadershipCard
                      key={index}
                      leader={leader}
                      index={index}
                    />
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Counters Section */}
        <div className="bg-gradient-to-r from-sky-100 via-indigo-300 to-indigo-100 rounded-xl p-8 text-gray-800 shadow-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { value: 30, label: "Happy Clients" },
              { value: 40, label: "Leadership Experience (Years)" },
              { value: 20, label: "Team Members" },
              { value: 97, label: "Satisfaction", isPercent: true }
            ].map((item, index) => (
              <div key={index}>
                <Counter target={item.value} isPercent={item.isPercent} />
                <p className="text-gray-600">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutUs;